import { observable, computed, set } from 'mobx';
import { IPerlgem, IFinishOption } from '../../../api/ProdcatApiSdk';
import { IValue, IKeyValue } from './BasicEntities';
import { IMedia } from './Media';
import { ICoreShade } from './Shade';
import { ITag } from './Tag';
import { IPrice } from './Price';
import { ISku } from './Sku';
import { ISkuSkin } from './SkuSkin';
import { SkuShadeGroup } from './SkuShadeGroup';
import { ICrossSellCategory } from './CrossSellCategory';

export interface IProductSkuMap {
    // product data for each sku id
    [key: string]: ProductSku;
}

export interface IProductSku extends ISku {
    brand: string;
    display_name: string;
    product_id: string;
    product_url: string;
}

export const EMPTY_PRICE_DATA: IPrice = {
    currency: '',
    include_tax: {
        price: 0,
        price_per_unit: 0,
        original_price: 0,
        price_formatted: '',
        original_price_formatted: '',
        price_per_unit_formatted: ''
    },
    exclude_tax: {
        price: 0,
        price_per_unit: 0,
        original_price: 0,
        price_formatted: '',
        original_price_formatted: '',
        price_per_unit_formatted: ''
    },
    is_discounted: false
};

export interface IProductSkuResult {
    inventory_status: string;
    is_out_of_stock: boolean;
    is_replenishable: boolean;
    large_image: string;
    name: string;
    newPrice: number;
    oldPrice: number | null;
    perlgem: IPerlgem;
    preorder_date: string;
    price: number;
    product_id: string;
    shade: string;
    shadeCode: string;
    short_description: string;
    size: string;
    skuId: string;
    tags: {
        items: ITag[];
    };
    thumb: string;
    url: string;
}

export class ProductSku implements ISku {
    @observable public cross_sell: ICrossSellCategory;
    @observable public color_family: IKeyValue[];
    @observable public brand: string;
    @observable public default_category: string;
    @observable public finish_options: IFinishOption[];
    @observable public display_name: string;
    @observable public material_code: string;
    @observable public iln_listing: string;
    @observable public iln_version_number: string;
    @observable public inventory_status: string;
    @observable public is_engravable: boolean;
    @observable public is_default_sku: boolean;
    @observable public is_discountable: boolean;
    @observable public is_giftwrap: boolean;
    @observable public is_out_of_stock: boolean;
    @observable public is_preorderable: boolean;
    @observable public is_replenishable: boolean;
    @observable public is_shaded: boolean;
    @observable public is_sized: boolean;
    @observable public is_under_weight_hazmat: boolean;
    @observable public media: IMedia;
    @observable public out_of_stock_threshold: number;
    @observable public perlgem: IPerlgem;
    @observable public preorder_date: string;
    @observable public prices: IPrice[];
    @observable public product_id: string;
    @observable public product_size: string;
    @observable public product_url: string = '#';
    @observable public shades: ICoreShade[];
    @observable public shade_groups: SkuShadeGroup[];
    @observable public short_description: string;
    @observable public sizes: IKeyValue[];
    @observable public skin: ISkuSkin;
    @observable public sku_badge: string;
    @observable public sku_badge_image: string;
    @observable public sku_id: string;
    @observable public sku_url: string;
    @observable public sku_promo_messaging: string;
    @observable public tags: {
        items: ITag[];
    };
    @observable public undertone: IKeyValue[];
    @observable public unit_of_measure: string;
    @observable public unit_size: number;
    @observable public unit_size_formatted: string;
    @observable public upc_code: string;
    @observable public texture: IValue[];
    @observable public is_foundation_experience: boolean;
    @observable public is_color_experience: boolean;
    @observable public iln_formulated_without: string;
    private currency: string;

    public static fromJSON(data: IProductSku, currency: string) {
        const product = new ProductSku();
        set(product, { ...data, currency });

        return product;
    }

    @computed
    get skuId() {
        return this.sku_id;
    }

    @computed
    get thumb() {
        if (this.media && this.media.small && this.media.small.length) {
            return this.media.small[0].src;
        }

        return '';
    }

    @computed
    get largeImage() {
        if (this.media && this.media.large && this.media.large.length) {
            return this.media.large[0].src;
        }

        return '';
    }

    @computed
    get name() {
        return this.display_name;
    }

    @computed
    get size() {
        return this.sizes && this.sizes.length
            ? this.sizes.map((size) => size.value).join(' / ')
            : '';
    }

    @computed
    get shade() {
        return this.shades && this.shades.length ? this.shades[0].name : '';
    }

    @computed
    get shadeCode() {
        return this.shades && this.shades.length ? this.shades[0].hex_val : '';
    }

    @computed
    get url() {
        return this.product_url;
    }

    @computed
    get priceDataByCurrency(): IPrice {
        return this.prices.find((price) => price.currency === this.currency) || EMPTY_PRICE_DATA;
    }

    @computed
    get price() {
        return this.priceDataByCurrency.include_tax.price;
    }

    @computed
    get price_per_unit() {
        return this.priceDataByCurrency.include_tax.price_per_unit;
    }

    @computed
    get newPrice() {
        return this.price;
    }

    @computed
    get oldPrice() {
        return this.priceDataByCurrency.is_discounted
            ? this.priceDataByCurrency.include_tax.original_price
            : this.price;
    }

    public toJSON(): IProductSkuResult {
        return {
            inventory_status: this.inventory_status,
            is_out_of_stock: this.is_out_of_stock,
            is_replenishable: this.is_replenishable,
            large_image: this.largeImage,
            name: this.name,
            newPrice: this.newPrice,
            oldPrice: this.oldPrice,
            perlgem: this.perlgem,
            preorder_date: this.preorder_date,
            price: this.price,
            product_id: this.product_id,
            shade: this.shade,
            shadeCode: this.shadeCode,
            short_description: this.short_description,
            size: this.size,
            skuId: this.skuId,
            tags: this.tags,
            thumb: this.thumb,
            url: this.url
        };
    }
}
